<template>
    <div class="campaign-share">
        <h3 class="section-title mb-2">{{ title }}</h3>
        <p class="share-description mb-4">{{ description }}</p>

        <v-card elevation="0" color="#f4f5f6" class="pa-4 share-card">
            <v-row align="center">
                <v-col cols="12" sm="auto" class="d-flex justify-center justify-sm-start mb-3 mb-sm-0 share-icons">
                    <!-- SMS -->
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <a :href="smsShareUrl" class="share-link mr-3">
                                <v-avatar
                                    size="40"
                                    color="#25D366"
                                    v-bind="attrs"
                                    v-on="on"
                                    class="share-icon"
                                >
                                    <v-icon color="white" size="22">mdi-message-text</v-icon>
                                </v-avatar>
                            </a>
                        </template>
                        <span>Share via Text Message</span>
                    </v-tooltip>

                    <!-- Facebook -->
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <a
                                :href="facebookShareUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="share-link mr-3"
                                @click.prevent="openShareWindow(facebookShareUrl)"
                            >
                                <v-avatar
                                    size="40"
                                    color="#1877F2"
                                    v-bind="attrs"
                                    v-on="on"
                                    class="share-icon"
                                >
                                    <v-icon color="white" size="22">mdi-facebook</v-icon>
                                </v-avatar>
                            </a>
                        </template>
                        <span>Share on Facebook</span>
                    </v-tooltip>

                    <!-- X (Twitter) -->
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <a
                                :href="twitterShareUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="share-link mr-3"
                                @click.prevent="openShareWindow(twitterShareUrl)"
                            >
                                <v-avatar
                                    size="40"
                                    color="#000"
                                    v-bind="attrs"
                                    v-on="on"
                                    class="share-icon"
                                >
                                    <span class="x-logo">X</span>
                                </v-avatar>
                            </a>
                        </template>
                        <span>Share on X</span>
                    </v-tooltip>

                    <!-- Snapchat -->
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <a
                                href="#"
                                class="share-link mr-3"
                                v-clipboard="shareUrl"
                                @click.prevent="onSnapchatShare"
                            >
                                <v-img
                                    height="40"
                                    width="40"
                                    contain
                                    src="@/assets/icons/campaign-snapchat.svg"
                                    class="share-icon rounded-circle"
                                    v-bind="attrs"
                                    v-on="on"
                                />
                            </a>
                        </template>
                        <span>Share on Snapchat</span>
                    </v-tooltip>

                    <!-- Instagram -->
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <a
                                href="#"
                                class="share-link mr-3"
                                v-clipboard="shareUrl"
                                @click.prevent="onInstagramShare"
                            >
                                <v-avatar
                                    size="40"
                                    class="share-icon instagram-gradient"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon color="white" size="22">mdi-instagram</v-icon>
                                </v-avatar>
                            </a>
                        </template>
                        <span>Share on Instagram</span>
                    </v-tooltip>

                    <!-- Email -->
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <a :href="emailShareUrl" class="share-link mr-3">
                                <v-avatar
                                    size="40"
                                    color="#EA4335"
                                    v-bind="attrs"
                                    v-on="on"
                                    class="share-icon"
                                >
                                    <v-icon color="white" size="20">mdi-email-outline</v-icon>
                                </v-avatar>
                            </a>
                        </template>
                        <span>Share via Email</span>
                    </v-tooltip>

                    <!-- QR Code -->
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <a
                                href="#"
                                class="share-link"
                                @click.prevent="showQrDialog = true"
                            >
                                <v-avatar
                                    size="40"
                                    color="#6B46C1"
                                    v-bind="attrs"
                                    v-on="on"
                                    class="share-icon"
                                >
                                    <v-icon color="white" size="22">mdi-qrcode</v-icon>
                                </v-avatar>
                            </a>
                        </template>
                        <span>Get QR Code</span>
                    </v-tooltip>
                </v-col>

                <v-col cols="12" sm class="d-flex copy-link">
                    <v-text-field
                        :value="shareUrl"
                        outlined
                        dense
                        readonly
                        hide-details
                        background-color="white"
                        class="copy-input"
                    ></v-text-field>
                    <v-btn
                        color="primary"
                        depressed
                        height="40"
                        v-clipboard="shareUrl"
                        @click="onCopyLink"
                        class="copy-btn"
                    >
                        Copy
                    </v-btn>
                </v-col>
            </v-row>
        </v-card>

        <!-- QR Code Dialog -->
        <v-dialog v-model="showQrDialog" max-width="360">
            <v-card class="qr-dialog">
                <v-card-title class="justify-space-between">
                    <span class="qr-dialog-title">Your Campaign QR Code</span>
                    <v-btn icon @click="showQrDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="text-center pb-2">
                    <div class="qr-container" ref="qrContainer">
                        <qrcode-vue
                            :value="shareUrl"
                            :size="200"
                            level="H"
                            render-as="canvas"
                            class="qr-code"
                        />
                        <p class="qr-name mt-3 mb-0">{{ userName }}'s Campaign</p>
                    </div>
                    <p class="qr-instructions mt-3">
                        Scan this code to view and donate to the campaign
                    </p>
                </v-card-text>
                <v-card-actions class="justify-center pb-4">
                    <v-btn
                        color="primary"
                        depressed
                        @click="downloadQrCode"
                    >
                        <v-icon left>mdi-download</v-icon>
                        Download QR Code
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue'

export default {
    name: 'CampaignShare',
    components: {
        QrcodeVue
    },
    props: {
        shareUrl: {
            type: String,
            required: true
        },
        userName: {
            type: String,
            required: true
        },
        title: {
            type: String,
            default: 'Share this campaign'
        },
        description: {
            type: String,
            default: 'Help spread the word and make a bigger impact.'
        }
    },
    data() {
        return {
            showQrDialog: false
        }
    },
    computed: {
        shareTitle() {
            return `Help ${this.userName} raise funds for college`
        },
        shareDescription() {
            return `${this.userName} is raising funds for college expenses. Your support can make a difference!`
        },
        smsMessage() {
            return `Hey! ${this.userName} is raising funds for college and could use your support. Every little bit helps! Check out their campaign: ${this.shareUrl}`
        },
        facebookShareUrl() {
            const url = encodeURIComponent(this.shareUrl)
            const quote = encodeURIComponent(this.shareTitle)
            return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`
        },
        twitterShareUrl() {
            const url = encodeURIComponent(this.shareUrl)
            const text = encodeURIComponent(this.shareTitle)
            return `https://x.com/intent/tweet?url=${url}&text=${text}&hashtags=edurent`
        },
        smsShareUrl() {
            const body = encodeURIComponent(this.smsMessage)
            // Use different format for iOS vs Android
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
            return isIOS ? `sms:&body=${body}` : `sms:?body=${body}`
        },
        emailShareUrl() {
            const subject = encodeURIComponent(this.shareTitle)
            const body = encodeURIComponent(`${this.shareDescription}\n\n${this.shareUrl}`)
            return `mailto:?subject=${subject}&body=${body}`
        }
    },
    methods: {
        onCopyLink() {
            this.$store.commit('setSnackbar', {
                show: true,
                message: 'Link copied to clipboard!',
                color: 'success'
            })
        },
        onSnapchatShare() {
            this.$store.commit('setSnackbar', {
                show: true,
                message: 'Link copied! Share it on your Snapchat Story.',
                color: 'success'
            })
        },
        onInstagramShare() {
            this.$store.commit('setSnackbar', {
                show: true,
                message: 'Link copied! Paste it in your Instagram Story or bio.',
                color: 'success'
            })
        },
        openShareWindow(url) {
            const width = 600
            const height = 400
            const left = (window.innerWidth - width) / 2
            const top = (window.innerHeight - height) / 2
            window.open(
                url,
                'share',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
            )
        },
        downloadQrCode() {
            const container = this.$refs.qrContainer
            if (!container) return

            const canvas = container.querySelector('canvas')
            if (!canvas) return

            // Create a new canvas with padding and text
            const padding = 40
            const textHeight = 40
            const newCanvas = document.createElement('canvas')
            newCanvas.width = canvas.width + padding * 2
            newCanvas.height = canvas.height + padding * 2 + textHeight

            const ctx = newCanvas.getContext('2d')

            // White background
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, newCanvas.width, newCanvas.height)

            // Draw QR code
            ctx.drawImage(canvas, padding, padding)

            // Draw name text
            ctx.fillStyle = '#333333'
            ctx.font = 'bold 16px Arial'
            ctx.textAlign = 'center'
            const text = `${this.userName}'s Campaign`
            ctx.fillText(text, newCanvas.width / 2, canvas.height + padding + 30)

            // Download
            const link = document.createElement('a')
            const fileName = `${this.userName.replace(/\s+/g, '-')}-campaign-qr.png`
            link.download = fileName
            link.href = newCanvas.toDataURL('image/png')
            link.click()

            this.$store.commit('setSnackbar', {
                show: true,
                message: 'QR code downloaded!',
                color: 'success'
            })
        }
    }
}
</script>

<style scoped>
.section-title {
    font-size: 20px;
    font-weight: 700;
    color: #333;
}

.share-description {
    font-size: 14px;
    color: #666;
}

.share-card {
    border-radius: 10px !important;
}

.share-link {
    text-decoration: none;
    display: inline-block;
}

.share-icon {
    cursor: pointer;
    transition: transform 0.2s ease;
}

.share-icon:hover {
    transform: scale(1.1);
}

.x-logo {
    color: white;
    font-size: 20px;
    font-weight: 700;
    font-family: Arial, sans-serif;
}

.instagram-gradient {
    background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%);
}

.copy-link .copy-input {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.copy-link .copy-btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.copy-btn.v-btn .v-btn__content {
    color: #fff !important;
}

.qr-dialog {
    border-radius: 12px !important;
}

.qr-dialog-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.qr-container {
    display: inline-block;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
}

.qr-code {
    display: block;
    margin: 0 auto;
}

.qr-name {
    font-size: 14px;
    font-weight: 600;
    color: #333;
}

.qr-instructions {
    font-size: 13px;
    color: #666;
}
</style>
